import { useEffect, useRef, useState } from 'react';
import { Plus, Tag, X } from 'lucide-react';
import { tagService, type TagSuggestion } from '@services/tag.service';

interface TicketTagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Show usage counts beside suggestions. Default true. */
  showCounts?: boolean;
  /** Render in compact mode (used by sidebar). Default false. */
  compact?: boolean;
}

export function TicketTagInput({
  value,
  onChange,
  disabled,
  placeholder = 'Add a tag...',
  showCounts = true,
  compact = false,
}: TicketTagInputProps) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<TagSuggestion[]>([]);
  const [allSuggestions, setAllSuggestions] = useState<TagSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load all suggestions once on mount; refresh when value changes (so newly
  // auto-created tags appear in the dropdown next time it's opened).
  useEffect(() => {
    let cancelled = false;
    tagService
      .suggestions(undefined, 100)
      .then((list) => {
        if (!cancelled) setAllSuggestions(list);
      })
      .catch(() => {
        // suggestions are non-critical — silently fall back to plain input
      });
    return () => {
      cancelled = true;
    };
  }, [value.length]);

  // Re-filter on query change
  useEffect(() => {
    const q = query.trim().toLowerCase();
    const filtered = allSuggestions
      .filter((s) => !value.includes(s.name))
      .filter((s) => (q ? s.name.toLowerCase().includes(q) : true));
    setSuggestions(filtered.slice(0, 20));
  }, [query, allSuggestions, value]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const addTag = (raw: string) => {
    const tag = raw.trim();
    if (!tag || value.includes(tag)) return;
    onChange([...value, tag]);
    setQuery('');
    // Collapse the suggestion list after each add (no auto-refocus). It reopens
    // when the agent clicks/focuses the input again.
    setOpen(false);
  };

  const removeTag = (tag: string) => {
    onChange(value.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (query.trim()) addTag(query);
    } else if (e.key === 'Backspace' && !query && value.length > 0) {
      removeTag(value[value.length - 1]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const exactMatch = suggestions.some(
    (s) => s.name.toLowerCase() === query.trim().toLowerCase(),
  );
  const showCreateOption = query.trim().length > 0 && !exactMatch && !value.includes(query.trim());

  return (
    <div className="relative" ref={wrapperRef}>
      <div
        onClick={() => inputRef.current?.focus()}
        className={`flex flex-wrap content-start items-center gap-1.5 overflow-y-auto rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 ${
          compact ? 'p-1.5 min-h-[38px] max-h-24' : 'p-2 min-h-[42px] max-h-32'
        }`}
      >
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-200"
          >
            <Tag className="h-3 w-3" />
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              disabled={disabled}
              className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-50"
              aria-label={`Remove tag ${tag}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-50"
        />
      </div>

      {open && (suggestions.length > 0 || showCreateOption) && (
        <div
          // The form scrolls; without this the list opens below the fold.
          ref={(el) => el?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })}
          className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          {showCreateOption && (
            <button
              type="button"
              onClick={() => addTag(query)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Plus className="h-4 w-4 text-green-500" />
              <span>
                Create <span className="font-medium">"{query.trim()}"</span>
              </span>
            </button>
          )}
          {suggestions.map((s) => (
            <button
              key={s.name}
              type="button"
              onClick={() => addTag(s.name)}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <span className="flex items-center gap-2">
                <Tag className="h-3.5 w-3.5 text-gray-400" />
                {s.name}
              </span>
              {showCounts && (
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {s.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
